import React, { useMemo, useState } from 'react';
import { Calculator, PiggyBank, TrendingUp } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const initialInputs = {
  initialInvestment: '10000',
  monthlyContribution: '500',
  annualRate: '7',
  compoundingFrequency: '12',
  contributionFrequency: '12',
  years: '20',
  rateAdjustment: '0',
};

const formatCurrency = (value) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safeValue);
};

const frequencyOptions = [
  { label: 'compound.frequency.monthly', value: '12' },
  { label: 'compound.frequency.quarterly', value: '4' },
  { label: 'compound.frequency.semiAnnual', value: '2' },
  { label: 'compound.frequency.annual', value: '1' },
];

const buildSchedule = (inputs) => {
  const principal = Math.max(parseFloat(inputs.initialInvestment) || 0, 0);
  const contribution = Math.max(parseFloat(inputs.monthlyContribution) || 0, 0);
  const annualRate = (parseFloat(inputs.annualRate) || 0) / 100;
  const compoundingFrequency = Math.max(parseInt(inputs.compoundingFrequency, 10) || 12, 1);
  const contributionFrequency = Math.max(parseInt(inputs.contributionFrequency, 10) || 12, 1);
  const years = Math.max(parseFloat(inputs.years) || 0, 0);
  const rateAdjustment = (parseFloat(inputs.rateAdjustment) || 0) / 100;

  if (years === 0 || (principal === 0 && contribution === 0)) {
    return {
      schedule: [],
      futureValue: 0,
      totalContributions: 0,
      totalInterest: 0,
    };
  }

  const periodsPerYear = compoundingFrequency;
  const contributionPeriods = contributionFrequency;
  const totalPeriods = Math.round(years * periodsPerYear);
  const contributionAmount = contributionFrequency === contributionPeriods ? contribution
    : (contributionFrequency === 12 && contributionPeriods !== 12 ? (contribution * 12) / contributionPeriods : contribution);

  let balance = principal;
  let totalContributions = principal;
  const schedule = [];

  for (let period = 1; period <= totalPeriods; period += 1) {
    const currentYear = Math.floor((period - 1) / periodsPerYear);
    const effectiveRate = annualRate + rateAdjustment * currentYear;
    const periodRate = effectiveRate / periodsPerYear;

    const contributionThisPeriod = (period % Math.round(periodsPerYear / contributionPeriods) === 0)
      ? contributionAmount
      : 0;

    balance += contributionThisPeriod;
    totalContributions += contributionThisPeriod;
    balance *= 1 + periodRate;

    if (period % periodsPerYear === 0) {
      schedule.push({
        year: period / periodsPerYear,
        balance,
        contributions: totalContributions,
        interest: balance - totalContributions,
        effectiveRate: effectiveRate * 100,
      });
    }
  }

  const futureValue = balance;
  const totalInterest = futureValue - totalContributions;

  return {
    schedule,
    futureValue,
    totalContributions,
    totalInterest,
  };
};

const CompoundInterest = () => {
  const [inputs, setInputs] = useState(initialInputs);
  const results = useMemo(() => buildSchedule(inputs), [inputs]);
  const { t } = useTranslation();

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <PiggyBank className="h-5 w-5 text-primary-600" />
          {t('compound.title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('compound.description')}
        </p>
      </Card>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calculator className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('compound.section.inputs')}</h3>
            </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('compound.input.initialInvestment')}
                value={inputs.initialInvestment}
                onChange={(value) => handleInputChange('initialInvestment', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('compound.input.recurringContribution')}
                value={inputs.monthlyContribution}
                onChange={(value) => handleInputChange('monthlyContribution', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('compound.input.annualRate')}
                value={inputs.annualRate}
                onChange={(value) => handleInputChange('annualRate', value)}
                type="number"
              />
              <CalculatorInput
                label={t('compound.input.compoundingFrequency')}
                value={inputs.compoundingFrequency}
                onChange={(value) => handleInputChange('compoundingFrequency', value)}
                type="number"
              />
              <CalculatorInput
                label={t('compound.input.contributionFrequency')}
                value={inputs.contributionFrequency}
                onChange={(value) => handleInputChange('contributionFrequency', value)}
                type="number"
              />
              <CalculatorInput
                label={t('compound.input.yearsToGrow')}
                value={inputs.years}
                onChange={(value) => handleInputChange('years', value)}
                type="number"
              />
              <CalculatorInput
                label={t('compound.input.rateAdjustment')}
                value={inputs.rateAdjustment}
                onChange={(value) => handleInputChange('rateAdjustment', value)}
                type="number"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('compound.section.guidance')}</h3>
            </div>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li>{t('compound.guidance.adjustment')}</li>
              <li>{t('compound.guidance.frequency')}</li>
              <li>{t('compound.guidance.negative')}</li>
            </ul>
          </Card>
        </div>

        <div className="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-20 lg:self-start">
          <Card className="p-6 space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold uppercase tracking-wide">{t('compound.summary.title')}</h3>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('compound.summary.futureValue')}</div>
              <div className="bg-primary-100 text-primary-900 text-2xl font-bold px-4 py-2 rounded text-center">
                {formatCurrency(results.futureValue)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('compound.summary.totalContributions')}</div>
              <div className="text-xl font-bold text-center">
                {formatCurrency(results.totalContributions)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('compound.summary.growth')}</div>
              <div className="text-xl font-bold text-primary-700 dark:text-primary-400 text-center">
                {formatCurrency(results.totalInterest)}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {results.schedule.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('compound.table.title')}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
              <thead className="bg-gray-50 dark:bg-dark-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('compound.table.year')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('compound.table.balance')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('compound.table.contributions')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('compound.table.growth')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('compound.table.effectiveRate')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {results.schedule.map((row) => (
                  <tr key={row.year} className="bg-white dark:bg-dark-900">
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{row.year}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-semibold">
                      {formatCurrency(row.balance)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {formatCurrency(row.contributions)}
                    </td>
                    <td className="px-4 py-2 text-sm text-primary-700 dark:text-primary-400">
                      {formatCurrency(row.interest)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {row.effectiveRate.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {results.schedule.length === 0 && (
        <Card className="p-6 text-center text-gray-500 dark:text-gray-400">
          {t('compound.empty', 'Enter investment details to calculate compound growth.')}
        </Card>
      )}
    </div>
  );
};

export default CompoundInterest;
